/**
 * Screen 3 — the ordered list, the arithmetic behind it, and dinner.
 *
 * The API signs the crop URLs on the way out, so this screen renders straight
 * from `GET /api/scans/{id}` with no second query and no client-side join
 * beyond matching each ranked row to its dossier by `bottle_id`.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import Voice from '../components/Voice'
import { useVoicePlayback } from '../hooks/useVoicePlayback'
import { api } from '../lib/api'
import type { Dossier, PairingGuide, PanelAnswer, RankedEntry } from '../lib/types'

const AXIS_LABEL: Record<string, string> = {
  quality: 'Quality',
  value: 'Value',
  drink_now: 'Drink now',
  versatility: 'Versatility',
}

export default function Results() {
  const { runId } = useParams<{ runId: string }>()
  const navigate = useNavigate()
  const query = useQuery({
    queryKey: ['scan', runId],
    queryFn: () => api.getScan(runId!),
    enabled: Boolean(runId),
  })

  // A finished scan is the one thing that reliably changes the cellar, and
  // nothing else marks it stale. Without this the grid keeps serving whatever
  // it last fetched for the full `staleTime`, so if you glanced at an empty
  // cellar before scanning, the wines you have just ranked are not there when
  // you go back for them.
  const client = useQueryClient()
  const arrived = Boolean(query.data)
  useEffect(() => {
    if (arrived) void client.invalidateQueries({ queryKey: ['cellar'] })
  }, [arrived, client])

  if (query.isLoading) return <div className="card mt-6 h-64 animate-pulse opacity-40" />

  if (query.isError) {
    return (
      <div className="space-y-4 pt-16 text-center">
        <p className="text-sm text-rose-300">{(query.error as Error).message}</p>
        <button className="btn-ghost" onClick={() => navigate('/')}>
          Start again
        </button>
      </div>
    )
  }

  const document = query.data
  const result = document?.result
  const ranking = result?.ranking ?? document?.ranking ?? []
  const dossiers = new Map((result?.dossiers ?? []).map((d) => [d.bottle_id, d]))
  const pairing = result?.pairing_guide ?? document?.pairingGuide ?? null
  const [first, ...rest] = ranking
  // The result is the authority: it was written at the time and is what gets
  // replayed months later, so a run document that has been read back cannot
  // turn a shopping trip into a cellar scan.
  const shopping = (result?.mode ?? document?.mode) === 'shopping'
  const shelfAnswers = document?.shelfAnswers ?? []

  return (
    <div className="space-y-6 pt-5">
      {shopping && (
        <Shelf
          context={result?.user_context || document?.context || ''}
          note={document?.weightingNote ?? ''}
        />
      )}

      {result?.verdict && (
        <section className="card p-4 space-y-2">
          <VerdictAudio
            text={result.verdict}
            runId={runId}
            personaId={result?.persona_id ?? document?.personaId}
          />
          <p className="mt-2 text-[15px] leading-relaxed whitespace-pre-wrap text-stone-200">
            {result.verdict}
          </p>
        </section>
      )}

      {first && <Hero entry={first} dossier={dossiers.get(first.bottle_id)} />}

      <div className="space-y-2">
        {rest.map((entry) => (
          <Row key={entry.bottle_id} entry={entry} dossier={dossiers.get(entry.bottle_id)} />
        ))}
      </div>

      {pairing && <Pairing guide={pairing} ranking={ranking} />}

      {shopping && shelfAnswers.length > 0 && <Critics answers={shelfAnswers} />}

      {document?.status === 'complete' && !ranking.length && (
        <p className="pt-10 text-center text-sm text-stone-400">
          Nothing legible in those photographs. Try getting closer to the labels.
        </p>
      )}

      <button className="btn-ghost w-full" onClick={() => navigate('/')}>
        Scan another shelf
      </button>
    </div>
  )
}

/**
 * The banner that says none of this is yours.
 *
 * A shopping run looks exactly like a cellar scan otherwise — same ranking,
 * same dossiers, same verdict — and the difference matters enough to say out
 * loud, because the whole point is that nothing here went into your cellar.
 */
function Shelf({ context, note }: { context: string; note: string }) {
  return (
    <section className="card space-y-1 border-wine-700/40 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="pill border border-wine-500/40 bg-wine-800/40 text-wine-100">
          Out shopping
        </span>
        <span className="text-xs text-stone-500">Nothing was added to your cellar.</span>
      </div>
      {context && <p className="font-display text-lg leading-snug">“{context}”</p>}
      {note && <p className="text-xs text-stone-400">{note}</p>}
    </section>
  )
}

function Critics({ answers }: { answers: PanelAnswer[] }) {
  return (
    <section className="space-y-3">
      <h2 className="label">What the critics would buy</h2>
      {answers.map((answer) => (
        <Voice key={`${answer.personaId}-${answer.lensId}`} answer={answer} />
      ))}
    </section>
  )
}

function Hero({ entry, dossier }: { entry: RankedEntry; dossier?: Dossier }) {
  return (
    <section className="card overflow-hidden">
      {entry.cropUrl && (
        <img src={entry.cropUrl} alt="" className="h-56 w-full object-cover object-top" />
      )}
      <div className="space-y-3 p-4">
        <div className="flex items-baseline gap-2">
          <span className="font-display text-3xl text-wine-300">1</span>
          <h2 className="font-display text-xl leading-tight">{entry.display_name}</h2>
          <span className="ml-auto text-sm text-stone-400">
            {Math.round(entry.total_score)}
          </span>
        </div>
        <Provenance dossier={dossier} />
        {dossier?.style_note && <p className="text-sm text-stone-300">{dossier.style_note}</p>}
        <Axes entry={entry} />
        <Palate dossier={dossier} />
        <Why entry={entry} />
        <Footnotes entry={entry} dossier={dossier} />
      </div>
    </section>
  )
}

function Row({ entry, dossier }: { entry: RankedEntry; dossier?: Dossier }) {
  return (
    <article className="card p-3">
      <div className="flex gap-3">
        <span className="w-6 pt-1 text-center font-display text-xl text-stone-500">
          {entry.rank}
        </span>
        {entry.cropUrl && (
          <img
            src={entry.cropUrl}
            alt=""
            className="h-20 w-14 shrink-0 rounded-lg object-cover"
            loading="lazy"
          />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <h3 className="min-w-0 flex-1 truncate text-sm font-medium">
              {entry.display_name}
            </h3>
            <span className="text-xs text-stone-500">{Math.round(entry.total_score)}</span>
          </div>
          <Provenance dossier={dossier} />
          {dossier?.style_note && (
            <p className="mt-1 text-xs leading-relaxed text-stone-400">{dossier.style_note}</p>
          )}
          <Axes entry={entry} compact />
        </div>
      </div>
      <Why entry={entry} />
      <Footnotes entry={entry} dossier={dossier} />
    </article>
  )
}

function Provenance({ dossier }: { dossier?: Dossier }) {
  if (!dossier) return null
  const parts = [dossier.appellation ?? dossier.region, dossier.country, dossier.grapes?.[0]]
  const line = parts.filter(Boolean).join(' · ')
  if (!line) return null
  return <p className="text-[11px] tracking-wide text-stone-500 uppercase">{line}</p>
}

function Axes({ entry, compact }: { entry: RankedEntry; compact?: boolean }) {
  const axes = Object.entries(entry.axis_scores ?? {})
  if (!axes.length) return null
  return (
    <dl className={`mt-2 grid gap-x-4 gap-y-1 ${compact ? 'grid-cols-2' : 'grid-cols-2'}`}>
      {axes.map(([axis, value]) => (
        <div key={axis} className="flex items-center gap-2">
          <dt className="w-20 shrink-0 truncate text-[10px] tracking-wide text-stone-500 uppercase">
            {AXIS_LABEL[axis] ?? axis.replace(/_/g, ' ')}
          </dt>
          <dd className="h-1 flex-1 overflow-hidden rounded-full bg-white/8">
            <div
              className="h-full rounded-full bg-wine-500"
              style={{ width: `${Math.max(0, Math.min(100, Number(value)))}%` }}
            />
          </dd>
        </div>
      ))}
    </dl>
  )
}

function Palate({ dossier }: { dossier?: Dossier }) {
  const palate = dossier?.palate
  if (!palate) return null
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {palate.primary_flavours?.slice(0, 5).map((flavour) => (
        <span key={flavour} className="pill bg-white/5 text-stone-300">
          {flavour}
        </span>
      ))}
      <span className="pill bg-white/5 text-stone-400">{palate.serving_temp_c} °C</span>
      {palate.decant_minutes > 0 && (
        <span className="pill bg-white/5 text-stone-400">
          decant {palate.decant_minutes} min
        </span>
      )}
    </div>
  )
}

/**
 * The arithmetic, folded away. Shown on demand, because a ranking nobody can
 * interrogate is just an opinion with a number on it.
 */
function Why({ entry }: { entry: RankedEntry }) {
  const [open, setOpen] = useState(false)
  const contributions = Object.entries(entry.contributions ?? {})
  if (!contributions.length) return null

  return (
    <div className="mt-2">
      <button
        onClick={() => setOpen((was) => !was)}
        className="text-xs text-stone-500 underline decoration-dotted underline-offset-4 hover:text-stone-300"
      >
        {open ? 'Hide the arithmetic' : 'Why it ranked here'}
      </button>
      {open && (
        <table className="mt-2 w-full rounded-lg bg-black/25 p-3 text-xs text-stone-400">
          <tbody>
            {contributions.map(([axis, weighted]) => (
              <tr key={axis}>
                <td className="py-1 pl-3">{AXIS_LABEL[axis] ?? axis}</td>
                <td className="py-1 text-right text-stone-500">
                  {entry.axis_scores?.[axis as keyof typeof entry.axis_scores] ?? '—'}
                </td>
                <td className="py-1 pr-3 text-right tabular-nums text-stone-300">
                  +{weighted.toFixed(1)}
                </td>
              </tr>
            ))}
            <tr className="border-t border-white/8">
              <td className="py-1 pl-3 font-medium text-stone-300">Total</td>
              <td />
              <td className="py-1 pr-3 text-right font-medium tabular-nums text-stone-100">
                {entry.total_score.toFixed(1)}
              </td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  )
}

function Footnotes({ entry, dossier }: { entry: RankedEntry; dossier?: Dossier }) {
  const critics = dossier?.critic_scores ?? []
  const prices = dossier?.price_points ?? []
  const caveats = dossier?.caveats ?? []
  const cheapest = prices.length
    ? prices.reduce((low, price) => (price.amount < low.amount ? price : low))
    : null

  const ticket = dossier?.shelf_price

  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      {/* First, because in a shop the price on the rail is the only one you can
          actually pay. It has no source URL — it came off a photograph — which
          is why it is not a link like the web prices below it. */}
      {ticket && (
        <span
          className="pill border border-wine-500/40 bg-wine-800/40 text-wine-100"
          title={ticket.note ?? 'Read off the shelf ticket'}
        >
          {ticket.currency} {ticket.amount} · on the shelf
        </span>
      )}
      {entry.owned ? (
        <span className="pill bg-white/5 text-stone-300">
          you already have {entry.owned}
        </span>
      ) : null}
      {cheapest && (
        <a
          href={cheapest.source_url}
          target="_blank"
          rel="noreferrer"
          className="pill bg-white/5 text-stone-300 hover:text-stone-100"
        >
          {cheapest.currency} {cheapest.amount} · {cheapest.retailer}
        </a>
      )}
      {critics.slice(0, 2).map((critic) => (
        <a
          key={critic.source_url}
          href={critic.source_url}
          target="_blank"
          rel="noreferrer"
          className="pill bg-white/5 text-stone-300 hover:text-stone-100"
        >
          {critic.score} {critic.critic}
        </a>
      ))}
      {dossier?.evidence_level && dossier.evidence_level !== 'sourced' && (
        <span
          className="pill bg-amber-400/10 text-amber-200/90"
          title={caveats.join(' · ') || 'No sources were found for this bottle'}
        >
          {dossier.evidence_level}
        </span>
      )}
      {entry.is_new === false && entry.prior_sightings?.length ? (
        <span className="pill bg-white/5 text-stone-400">
          seen {entry.prior_sightings.length}× before
        </span>
      ) : null}
      {entry.wine_id && (
        <Link to={`/wine/${entry.wine_id}`} className="pill bg-white/5 text-wine-200">
          rate &amp; comment →
        </Link>
      )}
    </div>
  )
}

function Pairing({ guide, ranking }: { guide: PairingGuide; ranking: RankedEntry[] }) {
  const names = new Map(ranking.map((entry) => [entry.bottle_id, entry.display_name]))
  const order = guide.serving_order ?? []
  return (
    <section className="card p-4">
      <h2 className="label">And to eat</h2>
      {guide.menu_theme && (
        <p className="font-display mt-1 text-lg text-wine-100">{guide.menu_theme}</p>
      )}
      <ol className="mt-3 space-y-3">
        {(guide.suggestions ?? []).map((course, index) => (
          <li key={index} className="border-l-2 border-wine-700/60 pl-3">
            <p className="text-[11px] tracking-[0.14em] text-stone-500 uppercase">
              {course.course}
            </p>
            <p className="text-sm text-stone-100">{course.dish}</p>
            <p className="text-xs text-wine-200">
              with {names.get(course.bottle_id) ?? course.bottle_id}
            </p>
            <p className="mt-1 text-xs text-stone-400">{course.rationale}</p>
          </li>
        ))}
      </ol>
      {order.length > 1 && (
        <p className="mt-3 text-xs text-stone-500">
          Pour in this order: {order.map((id) => names.get(id) ?? id).join(' → ')}
        </p>
      )}
    </section>
  )
}

function VerdictAudio({
  text,
  runId,
  personaId,
}: {
  text: string
  runId?: string
  personaId?: string
}) {
  const voicesQuery = useQuery({ queryKey: ['voices'], queryFn: api.voices })
  const voices = voicesQuery.data?.voices ?? []
  const fallbackVoiceId = voicesQuery.data?.defaultId ?? 'enthusiast'
  // The verdict was written in this persona's voice (`app/agents/voice.py`) —
  // read it back the same way whenever that persona has a matching voice.
  const defaultVoiceId =
    personaId && voices.some((v) => v.id === personaId) ? personaId : fallbackVoiceId

  const [selectedVoiceId, setSelectedVoiceId] = useState<string>('')

  useEffect(() => {
    if (defaultVoiceId && !selectedVoiceId) {
      setSelectedVoiceId(defaultVoiceId)
    }
  }, [defaultVoiceId, selectedVoiceId])

  const voiceId = selectedVoiceId || defaultVoiceId
  const { isLoading, isPlaying, error, toggle } = useVoicePlayback(text, voiceId, runId)

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/5 pb-2">
      <h2 className="label">The verdict</h2>
      <div className="flex items-center gap-2">
        {voices.length > 1 && (
          <select
            value={voiceId}
            onChange={(e) => setSelectedVoiceId(e.target.value)}
            className="rounded-full border border-white/10 bg-black/40 px-2.5 py-1 text-xs text-stone-300 focus:border-wine-500 focus:outline-none"
          >
            {voices.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        )}
        <button
          onClick={() => void toggle()}
          disabled={isLoading}
          className="chip chip-on flex items-center gap-1.5 py-1 hover:bg-wine-600/30"
          title="Listen to verdict with ElevenLabs voice"
        >
          {isLoading ? (
            <>
              <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-wine-200 border-t-transparent" />
              <span>Generating…</span>
            </>
          ) : isPlaying ? (
            <>
              <span>⏸ Pause</span>
            </>
          ) : (
            <>
              <span>🔊 Listen</span>
            </>
          )}
        </button>
      </div>
      {error && <p className="w-full text-xs text-rose-400">{error}</p>}
    </div>
  )
}
